import Util from './../../util';
import markerIcons from './../../config/markerIcons';

// Cache rendered PNGs for a given color so we don't have to re-render them all the time
// Potential memory leak here, should be used carefully...
const cache = {
    car: {},
    handshake: {},
    onSiteAgent: {},
    driver: {},
    scheduledRide: {},
    relocationOrOnDemandRide: {},
    rideDestination: {},
    cluster: {}
};

let iconConfigCreator = v => v;

const colorRegex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/;
const darkenFactor = 0.7; // inspired from d3 source code

function darken(hexColor) {
    if (!colorRegex.test(hexColor)) {
        console.error(`MapIconRenderService: The given color ${hexColor} does not match the required format #abc or #abcdef.`); // eslint-disable-line
        return '#000000'; // avoid throwing any errors
    }

    let color = hexColor.substr(1);
    if (color.length === 3) {
        color = color
            .split('')
            .map(part => part + part)
            .join('');
    }

    color = parseInt(color, 16);
    let rgb = [(color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff];
    rgb = rgb.map(channel => Math.round(channel * darkenFactor));
    color = (rgb[0] << 16) + (rgb[1] << 8) + rgb[2];
    return '#' + color.toString(16).padStart(6, '0');
}

function encodeBase64(svg) {
    return 'data:image/svg+xml;base64,' + btoa(svg);
}

let renderSVG = function(color, mainSVG, iconSVG = null, base64 = true) {
    const svg = Util.string.replace(['[ICON]', '[COLOR0]', '[COLOR1]'], [iconSVG || '', color, darken(color).toUpperCase()], mainSVG);
    return base64 ? encodeBase64(svg) : svg;
};

function svg2png(svgStr, size) {
    return new Promise(resolve => {
        const img = document.createElement('img');
        svgStr = svgStr.replace('<svg ', `<svg width="${size.width}" height="${size.height}" `); // required for Firefox to render the image correctly
        img.src = encodeBase64(svgStr);

        img.onload = function() {
            const canvas = document.createElement('canvas');
            canvas.setAttribute('width', size.width);
            canvas.setAttribute('height', size.height);
            canvas.style.imageRendering = 'crisp-edges';

            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = false;
            ctx.drawImage(img, 0, 0, size.width, size.height);
            resolve(canvas.toDataURL('image/png'));
        };
    });
}

function createIconConfig(dataURL, dimensions, centering) {
    centering = centering || { horizontal: true, vertical: false }; // can't use default parameters here

    const size = Object.assign({}, dimensions);
    const anchor = {
        x: centering.horizontal ? Math.round(dimensions.width / 2) : dimensions.width,
        y: centering.vertical ? Math.round(dimensions.height / 2) : dimensions.height
    };
    return iconConfigCreator(dataURL, size, anchor);
}

function getSVGIcon(type, mainId, symbolId, centering, color, renderPNG) {
    color = color.toUpperCase();
    if (!cache[type][color]) {
        const main = markerIcons.main[mainId];
        const symbol = symbolId ? markerIcons.symbol[symbolId] : null;
        const svg = renderSVG(color, main.svg, symbol, !renderPNG);
        const image = renderPNG ? svg2png(svg, main.size) : svg;
        cache[type][color] = main.size ? createIconConfig(image, main.size, centering) : svg;
    }
    return cache[type][color];
}

export default {
    getCarPin: getSVGIcon.bind(null, 'car', 'pin', 'car', null),
    getHandshakePin: getSVGIcon.bind(null, 'handshake', 'pin', 'handshake', null),
    getOnSiteAgentPin: getSVGIcon.bind(null, 'onSiteAgent', 'pin', 'onSiteAgent', null),
    getDriverPin: getSVGIcon.bind(null, 'driver', 'pin', 'driver', null),
    getScheduledRideIcon: getSVGIcon.bind(null, 'scheduledRide', 'circle', 'scheduledRide', { horizontal: true, vertical: true }),
    getOnDemandRideIcon: getSVGIcon.bind(null, 'relocationOrOnDemandRide', 'circle', null, { horizontal: true, vertical: true }),
    getRelocationIcon: getSVGIcon.bind(null, 'relocationOrOnDemandRide', 'circle', null, { horizontal: true, vertical: true }),
    getRideDestinationIcon: getSVGIcon.bind(null, 'rideDestination', 'diamond', null, { horizontal: true, vertical: true }),
    getClusterIcon: getSVGIcon.bind(null, 'cluster', 'cluster', null, null),

    setIconConfigCreator(creatorFunc) {
        iconConfigCreator = creatorFunc;
    },
    clear() {
        Object.keys(cache).forEach(key => {
            cache[key] = {};
        });
    },

    // Just for testing!
    __darken: darken,
    __setRenderFunction(func) {
        renderSVG = func;
    }
};
